/**
 * Property-name consolidation map.
 *
 * Some analytics properties were historically emitted under two spellings for the
 * same value (`env`/`environment`, and camelCase/snake_case id pairs), which splits
 * the data and breaks joins. This module is the single source of truth for the ONE
 * canonical (snake_case) spelling of each such property, plus the legacy alias(es)
 * still emitted for backward compatibility during the parity window.
 *
 * The resolver computes each value ONCE, writes the canonical key, then mirrors the
 * alias from that one canonical value via {@link applyLegacyPropertyAliases}. Aliases
 * are never written independently, so the two spellings can no longer diverge.
 *
 * Per rule: think-in-systems (ai.knowledge/global) — one canonical shape, one shared
 * mechanism; the alias mirror is isolated here so the gated Removal ticket can delete
 * this file (and the alias JSDoc in types/properties.ts) without touching the resolver.
 */

import {
  AGENT_PROPERTIES,
  COMMON_PROPERTIES,
  DEVICE_PROPERTIES,
  STUDIO_PROPERTIES
} from './event-properties.js';
import type { EventProperties } from '../types/properties.js';

/**
 * Canonical snake_case property → the legacy alias(es) mirrored from it during the
 * parity window. Removing the aliases is a separate gated change because the
 * compatibility contract forbids a hard cutover.
 *
 * `env` (not `environment`) is canonical because the repo's `canonical-event-map.json`
 * lists `env` as a required property across events and never `environment`.
 *
 * Device brand/model spellings (`brand`/`device_brand`, `model`/`device_model`) are
 * intentionally NOT here: they are the zero-emit, gate-forbidden legacy handled by the
 * canonical device-prop gate (`assertCanonicalDeviceProps`) and its
 * `OFF_CONVENTION_DEVICE_PROPERTY_KEYS`. Dual-emitting them would contradict that gate.
 */
export const LEGACY_PROPERTY_ALIASES = {
  [COMMON_PROPERTIES.ENV]: [COMMON_PROPERTIES.ENVIRONMENT],
  [COMMON_PROPERTIES.USER_ID_CANONICAL]: [COMMON_PROPERTIES.USER_ID],
  [COMMON_PROPERTIES.SESSION_ID_CANONICAL]: [COMMON_PROPERTIES.SESSION_ID],
  [DEVICE_PROPERTIES.DEVICE_ID_CANONICAL]: [DEVICE_PROPERTIES.DEVICE_ID],
  [AGENT_PROPERTIES.AGENT_ID_CANONICAL]: [AGENT_PROPERTIES.AGENT_ID],
  [STUDIO_PROPERTIES.STUDIO_ID_CANONICAL]: [STUDIO_PROPERTIES.STUDIO_ID]
} as const satisfies Record<string, readonly string[]>;

/** The canonical snake_case spelling of every consolidated property. */
export type CanonicalPropertyKey = keyof typeof LEGACY_PROPERTY_ALIASES;

// Stable [canonical, aliases] view for iteration without re-deriving the map shape.
const LEGACY_PROPERTY_ALIAS_ENTRIES: ReadonlyArray<readonly [string, readonly string[]]> =
  Object.entries(LEGACY_PROPERTY_ALIASES);

/**
 * Mirror each canonical property's value onto its legacy alias(es), returning a new
 * bag. Only mirrors when the canonical value is present, so an absent property adds
 * nothing. This keeps the legacy spelling emitting (backward compatibility) while the
 * canonical spelling is the sole source of truth — the alias can no longer diverge.
 *
 * Uses `Reflect.get` for the dynamic canonical-key read so no `as` cast is needed.
 * Per rule: reference-rules-in-comments (ai.knowledge/global).
 */
export function applyLegacyPropertyAliases(properties: EventProperties): EventProperties {
  const withAliases: EventProperties = { ...properties };

  for (const [canonicalKey, aliasKeys] of LEGACY_PROPERTY_ALIAS_ENTRIES) {
    const canonicalValue: unknown = Reflect.get(withAliases, canonicalKey);

    if (canonicalValue === undefined) {
      continue;
    }

    for (const aliasKey of aliasKeys) {
      Reflect.set(withAliases, aliasKey, canonicalValue);
    }
  }

  return withAliases;
}
